// src/component/MyProfileAvatar.jsx
import React, {
    useState,
    useEffect,
    useRef,
    useContext,
    memo,
    useCallback,
  } from "react";
  import { getDoc, doc, updateDoc } from "firebase/firestore";
  import {
    ref as storageRef,
    getDownloadURL,
    uploadBytes,
    deleteObject,
  } from "firebase/storage";
  import {
    Box,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert,
    Tooltip,
  } from "@mui/material";
  
  import { db, auth, storage } from "../api/firebase";
  import PicLoaderW40 from "./PicLoaderW40";
  import Camera from "../data/svg/Camera";
  import heic2any from "heic2any";
  import { CelebrationAnimation } from "./CelebrationAnimation";
  import { AppThemeContext } from "./AppShell";
  
  /**
   * MyProfile 専用のアバターコンポーネント
   *
   * props:
   *   userId?: string               // 指定がなければ auth.currentUser.uid
   *   size?: number                 // アバター直径(px) デフォルト 120
   *   editable?: boolean            // クリックで変更可能かどうか
   */
  const MyProfileAvatar = ({ userId, size = 120, editable = true }) => {
    const theme = useContext(AppThemeContext) || {};
    const accent = theme.railActive || "#5A35FF";
    const surface = theme.surfaceBg || "#FFFFFF";
  
    const [user, setUser] = useState(null);
    const [avatarUrl, setAvatarUrl] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
  
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [isConverting, setIsConverting] = useState(false);
  
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
  
    const [celebrationOpen, setCelebrationOpen] = useState(false);
    const [celebrationMessage, setCelebrationMessage] = useState("");
  
    const fileInputRef = useRef(null);
  
    const currentUserId = userId || auth.currentUser?.uid || null;
  
    /* ========== Firestore ユーザ情報取得 ========== */
    useEffect(() => {
      if (!currentUserId) {
        setIsLoadingUser(false);
        return;
      }
  
      const run = async () => {
        try {
          const snap = await getDoc(doc(db, "users", currentUserId));
          if (snap.exists()) {
            const data = snap.data();
            setUser(data);
            setAvatarUrl(data.avatar || "");
          } else {
            console.warn("MyProfileAvatar: user doc does not exist");
          }
        } catch (e) {
          console.error("MyProfileAvatar getDoc error:", e);
        } finally {
          setIsLoadingUser(false);
        }
      };
  
      run();
    }, [currentUserId]);
  
    const openSnack = useCallback((msg) => {
      setSnackbarMessage(msg);
      setSnackbarOpen(true);
    }, []);
  
    const handleSnackClose = (_, reason) => {
      if (reason === "clickaway") return;
      setSnackbarOpen(false);
    };
  
    /* ========== 画像選択 ========== */
    const handleClickAvatar = () => {
      if (!editable) return;
      if (!fileInputRef.current) return;
      fileInputRef.current.click();
    };
  
    const handleFileChange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
  
      const allowedTypes = ["image/jpeg", "image/png", "image/heic", "image/heif"];
      if (!allowedTypes.includes(file.type)) {
        openSnack("JPEG / PNG / HEIC の画像を選択してください。");
        return;
      }
  
      let processedFile = file;
  
      // HEIC / HEIF → JPEG 変換
      if (file.type === "image/heic" || file.type === "image/heif") {
        setIsConverting(true);
        try {
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.8,
          });
          processedFile = new File([convertedBlob], file.name.replace(/\.\w+$/, ".jpg"), {
            type: "image/jpeg",
          });
        } catch (err) {
          console.error("HEIC convert error:", err);
          openSnack("HEIC 画像の変換に失敗しました。別の形式でお試しください。");
          setIsConverting(false);
          return;
        }
        setIsConverting(false);
      }
  
      // 1MB 超なら簡易リサイズ
      try {
        if (processedFile.size > 1024 * 1024) {
          processedFile = await resizeImage(processedFile);
        }
      } catch (err) {
        console.error("resize error:", err);
        openSnack("画像のリサイズに失敗しました。");
        return;
      }
  
      setSelectedFile(processedFile);
    };
  
    /* ========== 画像リサイズ処理 ========== */
    const resizeImage = (file) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
  
      return new Promise((resolve, reject) => {
        reader.onload = (ev) => {
          const image = new Image();
          image.src = ev.target.result;
          image.onload = () => {
            const canvas = document.createElement("canvas");
            const maxWidth = 800;
            const scale = maxWidth / image.width;
            canvas.width = maxWidth;
            canvas.height = image.height * scale;
  
            const ctx = canvas.getContext("2d");
            ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error("Failed to create resized blob"));
                  return;
                }
                const resizedFile = new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                });
                resolve(resizedFile);
              },
              "image/jpeg",
              0.7
            );
          };
          image.onerror = (error) => reject(error);
        };
        reader.onerror = (error) => reject(error);
      });
    };
  
    /* ========== アップロード処理 ========== */
    useEffect(() => {
      if (!selectedFile || !currentUserId) return;
  
      const upload = async () => {
        setIsUploading(true);
  
        try {
          const path = `avatar/${Date.now()}-${selectedFile.name}`;
          const ref = storageRef(storage, path);
          const snap = await uploadBytes(ref, selectedFile);
          const url = await getDownloadURL(snap.ref);
  
          const userRef = doc(db, "users", currentUserId);
          const snapDoc = await getDoc(userRef);
          const prevData = snapDoc.exists() ? snapDoc.data() : {};
  
          // 旧画像があれば削除
          if (prevData.avatarPath) {
            try {
              await deleteObject(storageRef(storage, prevData.avatarPath));
            } catch (e) {
              console.warn("Failed to delete old avatar:", e);
            }
          }
  
          // Firestore 更新
          await updateDoc(userRef, {
            avatar: url,
            avatarPath: snap.ref.fullPath,
            isRegisterAvatar: true,
          });
  
          setAvatarUrl(url);
          openSnack("プロフィール画像を更新しました。");
          setCelebrationMessage("プロフィール画像が新しくなりました！");
          setCelebrationOpen(true);
          setTimeout(() => setCelebrationOpen(false), 8000);
        } catch (e) {
          console.error("avatar upload error:", e);
          openSnack("画像のアップロードに失敗しました。時間をおいて再度お試しください。");
        } finally {
          setIsUploading(false);
          setSelectedFile(null);
        }
      };
  
      upload();
    }, [selectedFile, currentUserId, openSnack]);
  
    /* ========== レンダリング ========== */
  
    const showLoader = isLoadingUser || (!avatarUrl && !user);
  
    const outerSize = size + 20; // 光の輪＋余白
  
    return (
      <>
        <Box
          sx={{
            position: "relative",
            width: outerSize,
            height: outerSize,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* ぼんやり光る背景 */}
          <Box
            sx={{
              position: "absolute",
              width: outerSize,
              height: outerSize,
              borderRadius: "50%",
              background: `radial-gradient(circle at 30% 20%, ${accent}55, transparent 60%)`,
              filter: "blur(4px)",
              opacity: 0.9,
            }}
          />
  
          {/* グラデーションリング */}
          <Box
            sx={{
              position: "absolute",
              width: size + 10,
              height: size + 10,
              borderRadius: "50%",
              background: `conic-gradient(from 180deg at 50% 50%, ${accent}99, ${accent}11, ${accent}BB)`,
              opacity: 0.75,
            }}
          />
  
          {/* 中央の白い円（カード） */}
          <Box
            sx={{
              position: "relative",
              width: size,
              height: size,
              borderRadius: "50%",
              background: surface,
              boxShadow:
                "0 18px 40px rgba(0,0,0,0.35), 0 0 0 3px rgba(255,255,255,0.9)",
              overflow: "hidden",
              cursor: editable ? "pointer" : "default",
            }}
            onClick={handleClickAvatar}
          >
            {showLoader ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PicLoaderW40 />
              </Box>
            ) : (
              <Box
                component="img"
                src={avatarUrl}
                alt="プロフィール画像"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            )}
  
            {/* ホバー時のカメラオーバーレイ */}
            {editable && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0,0,0,0.08)",
                  opacity: 0,
                  transition: "opacity 0.2s ease",
                  "&:hover": {
                    opacity: 1,
                    bgcolor: "rgba(0,0,0,0.3)",
                  },
                }}
              >
                <Tooltip title="プロフィール画像を変更">
                  <IconButton
                    sx={{
                      bgcolor: "rgba(255,255,255,0.9)",
                      "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    }}
                  >
                    <Camera />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
  
          {/* アップロード中インジケータ */}
          {isUploading || isConverting ? (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(0,0,0,0.25)",
                borderRadius: "50%",
              }}
            >
              <CircularProgress size={32} sx={{ color: "#fff" }} />
            </Box>
          ) : null}
  
          {/* hidden file input */}
          {editable && (
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.heic,.heif"
              hidden
              onChange={handleFileChange}
            />
          )}
        </Box>
  
        {/* Snackbar & Celebration */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={handleSnackClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleSnackClose} severity="success" sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
  
        <CelebrationAnimation
          isOpen={celebrationOpen}
          setIsOpen={setCelebrationOpen}
          message={celebrationMessage}
        />
      </>
    );
  };
  
  export default memo(MyProfileAvatar);
  